/**
 * NPT-2083 — Help Scout Beacon (chat widget) loader and helpers.
 *
 * The loader mirrors Help Scout's embed snippet: `window.Beacon` starts as a stub that queues
 * calls in `readyQueue`, and beacon-v2.helpscout.net replays them once it loads. Unlike the
 * snippet, the script is only injected after the page has loaded and the browser is idle, so it
 * never competes with checkout or the hotel search for the network.
 *
 * The Beacon id comes from site config (`helpScoutBeaconId`); a blank id means chat is off.
 * Every domain the Beacon uses must be in the CloudFront CSP (npt-terraform cloud-front-ssr).
 */

export const HELP_SCOUT_BEACON_SRC = 'https://beacon-v2.helpscout.net';
const SCRIPT_ID = 'helpscout-beacon-script';

type BeaconQueueItem = { method: string; options?: unknown; data?: unknown };
type BeaconFn = ((method: string, options?: unknown, data?: unknown) => void) & { readyQueue?: BeaconQueueItem[] };

declare global {
   interface Window {
      Beacon?: BeaconFn;
   }
}

export type BeaconIdentity = { name?: string; email: string; signature?: string };

/** Calls the Beacon if the stub exists. Safe before the script finishes loading: calls queue. */
export const beacon = (method: string, options?: unknown, data?: unknown): void => {
   if (typeof window === 'undefined' || typeof window.Beacon !== 'function') return;
   window.Beacon(method, options, data);
};

/** Installs the queueing stub and the script tag once. Idempotent. */
export const installBeaconScript = (): void => {
   if (typeof window === 'undefined') return;
   if (typeof window.Beacon !== 'function') {
      const stub: BeaconFn = (method, options, data) => {
         stub.readyQueue!.push({ method, options, data });
      };
      stub.readyQueue = [];
      window.Beacon = stub;
   }
   if (document.getElementById(SCRIPT_ID)) return;
   const script = document.createElement('script');
   script.id = SCRIPT_ID;
   script.type = 'text/javascript';
   script.async = true;
   script.src = HELP_SCOUT_BEACON_SRC;
   document.head.appendChild(script);
};

/**
 * Runs `cb` once the page has finished loading and the main thread is idle (or after
 * `timeoutMs` at the latest). Returns a cancel function.
 */
export const whenPageIdle = (cb: () => void, timeoutMs = 4000): (() => void) => {
   let cancelled = false;
   let idleHandle: number | undefined;
   let timer: ReturnType<typeof setTimeout> | undefined;

   const schedule = () => {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === 'function') {
         idleHandle = window.requestIdleCallback(() => !cancelled && cb(), { timeout: timeoutMs });
      } else {
         timer = setTimeout(() => !cancelled && cb(), 1500);
      }
   };

   if (document.readyState === 'complete') schedule();
   else window.addEventListener('load', schedule, { once: true });

   return () => {
      cancelled = true;
      window.removeEventListener('load', schedule);
      if (idleHandle !== undefined && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleHandle);
      if (timer) clearTimeout(timer);
   };
};

/** Platform admin screens are where support works, not where travellers ask for help. */
export const isBeaconSuppressedPath = (pathname: string): boolean => pathname === '/admin' || pathname.startsWith('/admin/');

const MY_TRIP_DETAIL = /^\/my-trips\/([^/?#]+)\/?$/;

/** The booking id when the traveller is on a My Trips detail page. */
export const getTripIdFromPath = (pathname: string): string | null => MY_TRIP_DETAIL.exec(pathname)?.[1] ?? null;

/**
 * What the support agent sees beside the conversation. Help Scout takes string values only
 * (max 20 entries), so empty values are dropped rather than sent as blanks.
 */
export const buildBeaconSessionData = ({
   siteName,
   userId,
   pageUrl,
   tripId,
   origin,
}: {
   siteName?: string | null;
   userId?: string | null;
   pageUrl: string;
   tripId?: string | null;
   origin: string;
}): Record<string, string> => {
   const data: Record<string, string> = {
      Site: siteName || '',
      'User ID': userId || '',
      Page: pageUrl,
      'Booking ID': tripId || '',
      'Admin trip panel': tripId ? `${origin}/admin/manage-trips/${encodeURIComponent(tripId)}` : '',
   };
   return Object.fromEntries(Object.entries(data).filter(([, value]) => value));
};
